import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
  MenuItem,
  Select,
  Chip,
  FormControl,
  InputLabel,
  Autocomplete,
  LinearProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import PropTypes from "prop-types";
import SyncIcon from "@mui/icons-material/Sync";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ScriptFolderSelector from "./ScriptFolderSelector";
import UploadQuestionAndScheme from "./UploadQuestionAndScheme";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
const ShowCourseDialog = ({
  open,
  onClose,
  examScheduleMasterId,
  examDate,
  timetableId,
  programId,
  branchId,
  showSnackbar,
}) => {
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [staffList, setStaffList] = useState([]);
  const [allocations, setAllocations] = useState({}); // { rowId: [{external_staff_id, scriptAllotted}, ...] }
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const handleFolderSelected = (files) => {
    setSelectedFiles(files);
  };
  const user = JSON.parse(sessionStorage.getItem("user"));
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [selectedFiles, setSelectedFiles] = useState([]);
  // Fetch course data
  useEffect(() => {
    if (open && examScheduleMasterId && examDate && programId) {
      axios
        .get(
          `${API_BASE_URL}/api/exam-external-schedule/${clientId}/${collegeId}/${examScheduleMasterId}/courses`,
          {
            params: { exam_date: examDate, program_id: programId },
          },
          {
            headers: {
              "Cache-Control": "no-cache",
              Pragma: "no-cache",
              "If-Modified-Since": "0",
            },
          }
        )
        .then((res) => {
          if (res.data.success) {
            const mapped = res.data.data.map((item, index) => ({
              id: index + 1, // required by DataGrid
              sno: index + 1,
              course_id: item.course_id,
              branch_id: item.branch_id,
              course_code: item.course_code,
              course_name: item.course_name || "-",
              branch_name: item.branch_name || "-",
              semester_number: item.semester_number || "-",
              year_number: item.year_number || "-",
              program_name: item.program_name || "-",
              exam_slot_name: item.exam_slot?.description || "-",
              exam_type_name: item.exam_type_name || "N/A",
              exam_date: item.exam_date,
              student_count: item.student_count || 0,
              script_count: item.script_count || 0,
              exam_schedule_master_id: examScheduleMasterId,
              exam_schedule_detail_id: item.exam_schedule_detail_id,
              timetable_id: item.timetable_id,
            }));
            setRows(mapped);
            setFilteredRows(mapped);
          } else {
            setRows([]);
            showSnackbar?.("No courses found for selected date", "info");
          }
        })
        .catch((err) => {
          console.error("Error fetching courses:", err);
          showSnackbar?.("Failed to load courses", "error");
        });
    }
  }, [open, examScheduleMasterId, examDate, programId, branchId]);

  useEffect(() => {
    if (open && examScheduleMasterId && collegeId && clientId) {
      axios
        .get(
          `${API_BASE_URL}/api/exam-script-allocation/${clientId}/${collegeId}/${examScheduleMasterId}/list`
        )
        .then((res) => {
          if (res.data.success && res.data.data.length > 0) {
            const grouped = {};

            // Group allocations by course
            res.data.data.forEach((alloc) => {
              const courseId = alloc.exam_course_id;
              if (!grouped[courseId]) grouped[courseId] = [];
              grouped[courseId].push({
                valuation_script_allotment_id: alloc.valuation_script_allotment_id,
                external_staff_id: alloc.external_staff_id,
                scriptAllotted: alloc.scripts_allotted,
              });
            });

            // Map course_id → DataGrid row.id
            const allocationMap = {};
            rows.forEach((row) => {
              if (grouped[row.course_id]) {
                allocationMap[row.id] = grouped[row.course_id];
              }
            });

            setAllocations(allocationMap);
          }
        })
        .catch((err) => {
          console.error("Error loading existing allocations:", err);
        });
    }
  }, [open, examScheduleMasterId, rows]);

  // Fetch staff list
  const fetchStaffList = async () => {
    try {
      let staff_type = "T";

      const res = await axios.get(
        `${API_BASE_URL}/api/staff-external/${clientId}/${collegeId}/active`,
        {
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
            "If-Modified-Since": "0",
          },
        }
      );

      if (res.data.success) {
        setStaffList(res.data.data || []);
      } else {
        setStaffList([]);
      }
    } catch (err) {
      console.error(err);
      showSnackbar?.("Error fetching staff list", "error");
    }
  };

  useEffect(() => {
    if (open) {
      fetchStaffList();
    }
  }, [open]);

  // Search functionality
  useEffect(() => {
    const lower = searchText.toLowerCase().trim();
    if (!lower) return setFilteredRows(rows);
    setFilteredRows(
      rows.filter(
        (row) =>
          (row.course_name && row.course_name.toLowerCase().includes(lower)) ||
          (row.course_code && row.course_code.toLowerCase().includes(lower)) ||
          (row.branch_name && row.branch_name.toLowerCase().includes(lower)) ||
          (row.exam_type_name && row.exam_type_name.toLowerCase().includes(lower)) ||
          (row.semester_number &&
            row.semester_number !== "-" &&
            row.semester_number.toString().includes(lower)) ||
          (row.year_number && row.year_number !== "-" && row.year_number.toString().includes(lower))
      )
    );
  }, [searchText, rows]);

  const toRoman = (num) => {
    const romans = ["", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];
    return romans[num] || num || "-";
  };
  const uploadFilesToServer = async (file, type, params) => {
    try {
      if (!file) {
        showSnackbar?.("No file selected", "warning");
        return;
      }

      // Validate PDF on frontend
      if (!file.name.toLowerCase().endsWith(".pdf")) {
        showSnackbar?.("Only PDF files are allowed", "warning");
        return;
      }

      const formData = new FormData();
      formData.append("college_id", collegeId);
      formData.append("timetable_id", params.timetable_id);
      formData.append("file", file);

      const res = await axios.post(
        `${API_BASE_URL}/api/exam-script-allocation/upload-${type}`,
        formData
      );

      if (res.data.success) {
        showSnackbar?.(
          `${type === "question" ? "Question Paper" : "Scheme"} uploaded successfully`,
          "success"
        );
      } else {
        showSnackbar?.(res.data.message || "Upload failed", "error");
      }

      return res.data;
    } catch (err) {
      console.error("Upload Error:", err);

      let errorMessage = "File upload failed";

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      showSnackbar?.(errorMessage, "error");
    }
  };

  const handleAllocationChange = (rowId, index, key, value) => {
    setAllocations((prev) => {
      const rowAlloc = prev[rowId] ? [...prev[rowId]] : [];
      const current = rowAlloc[index] || {};

      rowAlloc[index] = {
        ...current,
        valuation_script_allotment_id:
          current.valuation_script_allotment_id !== undefined
            ? current.valuation_script_allotment_id
            : null, // keep or initialize null
        [key]: value,
      };

      return { ...prev, [rowId]: rowAlloc };
    });
  };

  const addNewAllocationRow = (rowId) => {
    setAllocations((prev) => {
      const rowAlloc = prev[rowId] ? [...prev[rowId]] : [];
      rowAlloc.push({
        valuation_script_allotment_id: null, // ✅ add this
        external_staff_id: "",
        scriptAllotted: "",
      });
      return { ...prev, [rowId]: rowAlloc };
    });
  };

  const handleConfirmRow = async (rowId) => {
    const rowAlloc = allocations[rowId] || [];

    const row = rows.find((r) => r.id === rowId);
    if (!row) return;

    const totalAllotted = rowAlloc.reduce((sum, a) => sum + (parseInt(a.scriptAllotted) || 0), 0);

    if (totalAllotted !== row.script_count) {
      showSnackbar?.(
        `Allocation mismatch for ${row.course_name}. Allotted (${totalAllotted}) ≠ Script count (${row.script_count})`,
        "warning"
      );
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/api/exam-script-allocation/confirm`, {
        client_id: clientId,
        college_id: collegeId,
        course_id: row.course_id,
        exam_schedule_master_id: row.exam_schedule_master_id,
        exam_schedule_detail_id: row.exam_schedule_detail_id,
        timetable_id: row.timetable_id,
        allocations: rowAlloc.map((a) => ({
          valuation_script_allotment_id: a.valuation_script_allotment_id ?? null,
          external_staff_id: a.external_staff_id,
          scriptAllotted: a.scriptAllotted,
        })),
        user_email: user.user_email,
      });

      showSnackbar?.(`${row.course_name} allocations confirmed successfully`, "success");
    } catch (err) {
      // Safely extract backend message
      let errorMessage = "Failed to confirm allocations";

      if (err.response?.data?.error) {
        // When backend sends { error: "Some message" }
        errorMessage = err.response.data.error;
      } else if (err.response?.data?.message) {
        // When backend sends { message: "Some message" }
        errorMessage = err.response.data.message;
      } else if (err.message) {
        // Fallback: Axios network or JS error
        errorMessage = err.message;
      }

      showSnackbar?.(`${row.course_name}: ${errorMessage}`, "error");
    }
  };
  const handleConfirmAll = async () => {
    const invalidRows = [];

    const payload = rows.map((row) => {
      const rowAlloc = (allocations[row.id] || []).map((a) => ({
        valuation_script_allotment_id: a.valuation_script_allotment_id ?? null,
        external_staff_id: a.external_staff_id,
        scriptAllotted: a.scriptAllotted,
      }));

      const totalAllotted = rowAlloc.reduce((sum, a) => sum + (parseInt(a.scriptAllotted) || 0), 0);
      if (totalAllotted !== row.script_count) invalidRows.push(row.course_name);

      return {
        client_id: clientId,
        college_id: collegeId,
        course_id: row.course_id,
        exam_schedule_master_id: row.exam_schedule_master_id,
        exam_schedule_detail_id: row.exam_schedule_detail_id,
        timetable_id: row.timetable_id,
        allocations: rowAlloc,
        user_email: user.user_email,
      };
    });

    if (invalidRows.length > 0) {
      showSnackbar?.(`Allocation mismatch for: ${invalidRows.join(", ")}.`, "warning");
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/api/exam-script-allocation/bulk-confirm`, payload);
      showSnackbar?.("All courses confirmed successfully!", "success");
    } catch (err) {
      console.error(err);
      showSnackbar?.("Bulk confirmation failed.", "error");
    }
  };

  const columns = [
    { field: "sno", headerName: "SNo", flex: 0.3, minWidth: 60 },
    {
      field: "course",
      headerName: "Branch & Course",
      flex: 1.5,
      minWidth: 250,
      renderCell: (params) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <strong>{params.row.branch_name}</strong>
          <strong>{params.row.course_code}</strong>
          <span>{params.row.course_name}</span>
        </div>
      ),
    },
    {
      field: "counts",
      headerName: "Students/Scripts",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Chip
            label={params.row.student_count || 0}
            color="primary"
            variant="outlined"
            size="large"
            sx={{
              fontWeight: "bold",
              borderRadius: "8px",
              bgcolor: "rgba(223, 80, 168, 0.1)",
            }}
          />

          <Chip
            label={params.row.script_count || 0}
            color="secondary"
            variant="outlined"
            size="large"
            sx={{
              fontWeight: "bold",
              borderRadius: "8px",
              bgcolor: "rgba(206, 224, 39, 0.1)",
            }}
          />
        </Box>
      ),
    },

    {
      field: "question_scheme",
      headerName: "QP / Scheme",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* Upload box */}
          <UploadQuestionAndScheme
            onQuestionPaperUpload={(files) => {
              uploadFilesToServer(files, "question", params.row).then(() => {
                params.row.question_uploaded = true;
              });
            }}
            onPaperSchemeUpload={(files) => {
              uploadFilesToServer(files, "scheme", params.row).then(() => {
                params.row.scheme_uploaded = true;
              });
            }}
          />

          {/* Icons */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {/* QP ICON */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {params.row.question_uploaded ? (
                <CheckCircleIcon sx={{ color: "green", fontSize: 20 }} />
              ) : (
                <CancelIcon sx={{ color: "grey", fontSize: 20 }} />
              )}
              <span style={{ fontSize: 12 }}>QP</span>
            </Box>

            {/* Scheme ICON */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {params.row.scheme_uploaded ? (
                <CheckCircleIcon sx={{ color: "green", fontSize: 20 }} />
              ) : (
                <CancelIcon sx={{ color: "grey", fontSize: 20 }} />
              )}
              <span style={{ fontSize: 12 }}>Scheme</span>
            </Box>
          </Box>
        </Box>
      ),
    },

    {
      field: "folder_sync",
      headerName: "Folder/Sync",
      flex: 1,
      minWidth: 130,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
          {/* 🔹 Upload Scripts Folder */}
          <ScriptFolderSelector
            onFolderSelected={(files) => {
              handleFolderSelected(files);
              showSnackbar?.(
                `Selected ${files.length} files for ${params.row.course_name}`,
                "success"
              );
            }}
          />

          {/* 🔹 Sync Button */}
          <Tooltip title="Sync Scripts">
            <IconButton
              color="primary"
              onClick={async () => {
                try {
                  if (!selectedFiles.length) {
                    showSnackbar?.("Please select a scripts folder first", "warning");
                    return;
                  }
                  setUploading(true);
                  setUploadProgress(0);
                  const formData = new FormData();
                  formData.append("college_id", collegeId);
                  formData.append("timetable_id", params.row.timetable_id);
                  selectedFiles.forEach((file) => formData.append("files", file));

                  const res = await axios.post(
                    `${API_BASE_URL}/api/exam-script-allocation/sync-upload`,
                    formData,
                    {
                      headers: { "Content-Type": "multipart/form-data" },
                      timeout: 300000, // 5 minutes
                      maxBodyLength: Infinity,
                      maxContentLength: Infinity,
                      onUploadProgress: (e) => {
                        if (e.total) {
                          const percent = Math.round((e.loaded * 100) / e.total);
                          setUploadProgress(percent);
                        }
                      },
                    }
                  );
                  setUploading(false);
                  if (res.data.success) {
                    const updatedCount = res.data.script_count;
                    setRows((prev) =>
                      prev.map((r) =>
                        r.timetable_id === params.row.timetable_id
                          ? { ...r, script_count: updatedCount }
                          : r
                      )
                    );
                    showSnackbar?.(`Sync completed (${updatedCount} PDFs)`, "success");
                  } else {
                    showSnackbar?.("Sync failed", "error");
                  }
                } catch (err) {
                  console.error(err);

                  // ✅ Axios error handling
                  if (err.response && err.response.data) {
                    // Use custom error sent from backend
                    const apiError = err.response.data.error || "Sync failed";
                    showSnackbar?.(apiError, "error");
                  } else if (err.message) {
                    // Generic JS/network error
                    showSnackbar?.(err.message, "error");
                  } else {
                    showSnackbar?.("Sync failed", "error");
                  }
                }
              }}
            >
              <SyncIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },

    {
      field: "allocations",
      headerName: "Staff & Scripts Allotted",
      flex: 2.5,
      minWidth: 450,
      renderCell: (params) => {
        const rowId = params.row.id;
        const rowAlloc = allocations[rowId] || [{ external_staff_id: "", scriptAllotted: "" }];
        const totalAllotted = rowAlloc.reduce(
          (sum, a) => sum + (parseInt(a.scriptAllotted) || 0),
          0
        );

        // 🔹 delete specific allocation row
        const deleteAllocationRow = (rowId, index) => {
          setAllocations((prev) => {
            const updated = [...(prev[rowId] || [])];
            updated.splice(index, 1);
            return {
              ...prev,
              [rowId]: updated.length ? updated : [{ external_staff_id: "", scriptAllotted: "" }],
            };
          });
        };

        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
            {rowAlloc.map((alloc, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                  width: "100%",
                  flexWrap: "wrap",
                }}
              >
                {/* Staff Dropdown */}
                <FormControl sx={{ flex: 1, minWidth: 150 }}>
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={staffList}
                    getOptionLabel={(option) =>
                      option
                        ? `${option.staff_name} — ${option.department} — ${option.college_name}`
                        : ""
                    }
                    value={
                      staffList.find((s) => s.external_staff_id === alloc.external_staff_id) || null
                    }
                    onChange={(event, newValue) => {
                      handleAllocationChange(
                        rowId,
                        index,
                        "external_staff_id",
                        newValue?.external_staff_id || ""
                      );
                    }}
                    renderOption={(props, option) => (
                      <li {...props} style={{ display: "flex", flexDirection: "column" }}>
                        <strong>{option.staff_name}</strong>
                        <span style={{ fontSize: "12px", opacity: 0.8 }}>{option.department}</span>
                        <span style={{ fontSize: "12px", opacity: 0.8 }}>
                          {option.college_name}
                        </span>
                      </li>
                    )}
                    renderInput={(paramsInput) => (
                      <TextField
                        {...paramsInput}
                        margin="dense"
                        label="Select Staff"
                        InputLabelProps={{ shrink: true }}
                        onKeyDown={(e) => {
                          e.stopPropagation(); // 🔑 Prevent DataGrid / parent from hijacking SPACE
                        }}
                      />
                    )}
                  />
                </FormControl>

                {/* Allotted Input */}
                <TextField
                  label="Allotted"
                  type="number"
                  size="small"
                  value={alloc.scriptAllotted}
                  onChange={(e) => {
                    let value = parseInt(e.target.value) || 0;
                    const otherTotal = totalAllotted - (parseInt(alloc.scriptAllotted) || 0);
                    const newTotal = otherTotal + value;

                    if (newTotal > params.row.script_count) {
                      showSnackbar?.("Scripts exceed script count. Please adjust.", "warning");
                      value = params.row.script_count - otherTotal;
                    }

                    handleAllocationChange(rowId, index, "scriptAllotted", value);
                  }}
                  sx={{ width: 70 }}
                />

                {/* Delete Button */}
                {rowAlloc.length > 1 && (
                  <Button
                    size="small"
                    color="error"
                    onClick={() => deleteAllocationRow(rowId, index)}
                    sx={{ minWidth: 40 }}
                  >
                    ✕
                  </Button>
                )}
              </Box>
            ))}

            {/* Add Staff Button (only if totalAllotted < student_count) */}
            {rowAlloc.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 1,
                }}
              >
                {totalAllotted < params.row.script_count && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => addNewAllocationRow(rowId)}
                    sx={{
                      backgroundColor: "#379de6ff",
                      color: "#fff",
                      "&:hover": { backgroundColor: "#0e216fff" },
                    }}
                  >
                    + Add Staff
                  </Button>
                )}

                {(totalAllotted === params.row.script_count || rowAlloc.length > 0) && (
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    onClick={() => handleConfirmRow(params.row.id)}
                    sx={{ color: "white" }}
                  >
                    ✅ Confirm
                  </Button>
                )}
              </Box>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        {filteredRows.length > 0 ? (
          <>
            <Typography variant="h6">{filteredRows[0].program_name || "-"}</Typography>
            <Typography variant="body2" sx={{ color: "gray" }}>
              {toRoman(filteredRows[0].year_number)} Year |{" "}
              {toRoman(filteredRows[0].semester_number)} Semester | Exam Type:{" "}
              {filteredRows[0].exam_type_name || "-"} | Time:{" "}
              {filteredRows[0].exam_slot_name || "-"} | Date:{" "}
              {new Date(filteredRows[0].exam_date).toLocaleDateString()}
            </Typography>
          </>
        ) : (
          `Courses on ${new Date(examDate).toLocaleDateString()}`
        )}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
          {uploading && (
            <Box sx={{ width: "100%", mt: 1 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="caption" sx={{ mt: 0.5, display: "block" }}>
                Uploading... {uploadProgress}%
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            margin="normal"
            label="Search Course"
            variant="outlined"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </Box>

        {filteredRows.length === 0 ? (
          <Typography sx={{ mt: 3, textAlign: "center", color: "gray" }}>
            No courses available for the selected date and branch.
          </Typography>
        ) : (
          <Box sx={{ mt: 2 }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              autoHeight
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              rowsPerPageOptions={[5, 10, 25, 50]}
              disableRowSelectionOnClick
              pagination
              onCellClick={(params, event) => event.stopPropagation()}
              onRowClick={(params, event) => event.stopPropagation()}
              getRowHeight={(params) => {
                const rowId = params.id;
                const rowAlloc = allocations[rowId] || [
                  { external_staff_id: "", scriptAllotted: "" },
                ];
                const baseHeight = 100; // default row height for one allocation
                const allocationHeight = 60; // height per allocation row
                return baseHeight + allocationHeight * (rowAlloc.length - 1);
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          color="success"
          size="small"
          onClick={handleConfirmAll}
          sx={{
            backgroundColor: "#19d272ff",
            color: "#fff",
            "&:hover": { backgroundColor: "#91c864ff" },
          }}
        >
          ✅ Confirm All
        </Button>
        <Button
          onClick={onClose}
          variant="contained"
          size="small"
          sx={{
            backgroundColor: "#1976d2",
            color: "#fff",
            "&:hover": { backgroundColor: "#1565c0" },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ShowCourseDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  examScheduleMasterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  examDate: PropTypes.string.isRequired,
  timetableId: PropTypes.string.isRequired,
  programId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  branchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  showSnackbar: PropTypes.func,
};

export default ShowCourseDialog;
